import {
  type HeaderType,
  type PacketHeader,
  type PathLookupGraphPreset,
  isPacketHeaderSpbmBackbone,
  rgbColors,
} from '@ipfabric/utils';
import classNames from 'classnames';
import { useLocale } from 'containers/App/LocaleContext';
import { type FC, memo } from 'react';
import type { AppTranslations } from 'translations/translations';
import { distinct } from 'utils/array/distinct';
import type { PathLookupPacketDiff } from '../../deviceViewTypes';

const getProtocolColor = (
  preset: PathLookupGraphPreset,
  packetHeader: PacketHeader,
): string => {
  // The SPBM backbone header is carried as an ethernet header (type ===
  // 'ethernet'), so match it against its dedicated `spbmBackbone` edge rather
  // than the generic ethernet one. Presets saved before that edge existed fall
  // back to the plain ethernet colour below.
  if (isPacketHeaderSpbmBackbone(packetHeader)) {
    const backboneEdge = preset.edges.find(
      (edge) => edge.name === 'spbmBackbone',
    );
    if (backboneEdge) {
      return backboneEdge.style.color;
    }
  }
  const foundEdge = preset.edges.find(
    (edge) => edge.name === packetHeader.type,
  );
  return foundEdge?.style.color ?? rgbColors.black;
};

export type EdgeDiffType = 'removed' | 'added';

type PacketValue = string | number | null;

interface PacketDetailBodyProps {
  diff?: PathLookupPacketDiff;
  edgeDiffType?: EdgeDiffType;
  packetHeader: PacketHeader;
  packetIndex: number;
  pathLookupPreset: PathLookupGraphPreset;
}

const hasAttributeDiff = (
  diff: PathLookupPacketDiff,
  edgeDiffType: EdgeDiffType,
  packetIndex: number,
  key: string,
): boolean => {
  if (edgeDiffType === 'removed') {
    return (
      // Fixed noUncheckedIndexedAccess NIM-19408
      (diff.deleted[packetIndex] !== undefined &&
        key in diff.deleted[packetIndex]!) ||
      (diff.updated[packetIndex] !== undefined &&
        key in diff.updated[packetIndex]!)
    );
  }
  return (
    // Fixed noUncheckedIndexedAccess NIM-19408
    (diff.added[packetIndex] !== undefined &&
      key in diff.added[packetIndex]!) ||
    (diff.updated[packetIndex] !== undefined &&
      key in diff.updated[packetIndex]!)
  );
};

const isTcpFlagsAttribute = (type: HeaderType, attribute: string) =>
  type === 'tcp' && attribute === 'flags';

const makeTransformValue =
  (valueIsTcpFlag: boolean, msg: AppTranslations) => (value: PacketValue) => {
    if (value === null) {
      return msg.deviceView.pathLookup.unknown;
    }
    if (typeof value === 'string' && valueIsTcpFlag) {
      return value.toUpperCase();
    }
    if (typeof value === 'boolean') {
      return Boolean(value).toString();
    }
    return value;
  };

const getPacketHeaderHumanReadableValue = (
  value: PacketValue | PacketValue[],
  valueIsTcpFlag: boolean,
  msg: AppTranslations,
) => {
  if (Array.isArray(value)) {
    return value.length
      ? value
          .map(makeTransformValue(valueIsTcpFlag, msg))
          .filter((item) => item !== '')
          .join(', ')
      : msg.deviceView.pathLookup.none;
  }
  return makeTransformValue(valueIsTcpFlag, msg)(value);
};

const getPacketHeaderHumanReadableAttribute = (
  packetHeader: PacketHeader,
  packetHeaderAttribute: string,
): string => {
  // SPBM (802.1ah) backbone header: the ethernet fields carry Provider Backbone
  // values, so label them with the PBB names (B-DA / B-SA / B-VLAN / I-SID).
  if (isPacketHeaderSpbmBackbone(packetHeader)) {
    switch (packetHeaderAttribute) {
      case 'dst':
        return 'B-DA';
      case 'src':
        return 'B-SA';
      case 'vlan':
        return 'B-VLAN';
      case 'iSid':
        return 'I-SID';
      default:
        return packetHeaderAttribute;
    }
  }
  if (packetHeader.type === 'icmp') {
    switch (packetHeaderAttribute) {
      case 'icmpCode':
        return 'code';
      case 'icmpType':
        return 'type';
      default:
        return packetHeaderAttribute;
    }
  }
  return packetHeaderAttribute;
};

const BarePacketDetailBody: FC<PacketDetailBodyProps> = (props) => {
  const msg = useLocale();

  const modifiedKeys =
    props.diff !== undefined && props.edgeDiffType !== undefined
      ? Object.keys(
          props.diff[props.edgeDiffType === 'added' ? 'deleted' : 'added'][
            props.packetIndex
          ] ?? {},
        )
      : [];
  const packetHeaderTypes = distinct([
    ...Object.keys(props.packetHeader),
    ...modifiedKeys,
  ]).sort() as (keyof PacketHeader)[];
  return (
    <div className='device-view-packet-detail__row flex'>
      <div
        className={classNames(
          'device-view-packet-detail__header-type',
          'flex',
          'flex-col',
          'justify-center',
          'font-semibold',
          'align-middle',
          'w-lgu-20',
          'py-lgu-3',
        )}
      >
        <div className='rotate-180 text-center'>
          {isPacketHeaderSpbmBackbone(props.packetHeader)
            ? msg.deviceView.pathLookup.spbmBackboneHeaderType
            : msg.deviceView.pathLookup.headerType[props.packetHeader.type]}
        </div>
      </div>
      <div
        className='device-view-packet-detail__color'
        style={{
          background: getProtocolColor(
            props.pathLookupPreset,
            props.packetHeader,
          ),
        }}
      />
      <div className='py-lgu-2 flex flex-1 flex-col justify-center px-5'>
        {packetHeaderTypes.map((packetHeaderAttribute) => {
          if (packetHeaderAttribute === 'type') {
            return null;
          }
          if (props.packetHeader[packetHeaderAttribute] === undefined) {
            return <div key={packetHeaderAttribute}>&nbsp;</div>;
          }
          const attributeHasDiff =
            props.diff !== undefined &&
            props.edgeDiffType !== undefined &&
            hasAttributeDiff(
              props.diff,
              props.edgeDiffType,
              props.packetIndex,
              packetHeaderAttribute,
            );
          return (
            <div key={packetHeaderAttribute}>
              <span
                className={classNames(
                  'inline-block',
                  attributeHasDiff && 'device-view-packet-detail__diff',
                  attributeHasDiff &&
                    props.edgeDiffType === 'removed' &&
                    'device-view-packet-detail__diff--removed',
                  attributeHasDiff &&
                    props.edgeDiffType === 'added' &&
                    'device-view-packet-detail__diff--added',
                  attributeHasDiff && 'pr-lgu-5',
                )}
              >
                {attributeHasDiff && (
                  <span className='ml-lgu-2 mr-lgu-1 w-lgu-3 inline-block text-center'>
                    {props.edgeDiffType === 'removed' ? (
                      <span className='device-view-packet-detail__diff-minus'>
                        -
                      </span>
                    ) : (
                      <span className='device-view-packet-detail__diff-plus'>
                        +
                      </span>
                    )}
                  </span>
                )}
                <span>
                  {getPacketHeaderHumanReadableAttribute(
                    props.packetHeader,
                    packetHeaderAttribute,
                  )}
                  :{' '}
                  {getPacketHeaderHumanReadableValue(
                    props.packetHeader[packetHeaderAttribute],
                    isTcpFlagsAttribute(
                      props.packetHeader.type,
                      packetHeaderAttribute,
                    ),
                    msg,
                  )}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const PacketDetailBody = memo(BarePacketDetailBody);
